import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Plus, Trash2, Shield, Edit, Users, Loader2, X,
  Eye, EyeOff, Phone, Mail, MapPin, MessageCircle, ShoppingBag,
  Calendar, CreditCard, Copy, Check, Search, Filter,
  CheckCircle, XCircle, UserPlus,
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useBodyLock } from '../../hooks/useBodyLock';

// ---------- Interfaces ----------

interface Usuario {
  id: string | number;
  username: string;
  role: string;
  nivel?: string;
  created_at: string;
  email?: string;
  nome?: string;
  sobrenome?: string;
  telefone?: string;
  rua?: string;
  bairro?: string;
  numero_casa?: string;
  cep?: string;
  cidade?: string;
}

interface Garcom {
  id: number;
  nome: string;
  sobrenome: string;
  email: string;
  telefone?: string;
  senha?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

interface UsuarioForm {
  username: string;
  password: string;
  role: string;
  email?: string;
  nome?: string;
  sobrenome?: string;
}

interface ItemPedido {
  produto_nome: string;
  quantidade: number;
  preco_unitario: number;
  observacoes?: string;
}

interface Pedido {
  id: number;
  created_at: string;
  status: string;
  total: number;
  tipo_pedido: string;
  metodo_pagamento: string;
  total_itens: number;
  itens: ItemPedido[];
}

interface UserDetail {
  usuario: Usuario;
  pedidos: Pedido[];
  stats: {
    total_pedidos: number;
    total_gasto: number;
    ultimo_pedido: string | null;
  };
}

// ---------- Breakpoint hook ----------

type Breakpoint = 'mobile' | 'tablet' | 'desktop';

const getBreakpoint = (): Breakpoint => {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w >= 1024) return 'desktop';
  if (w >= 768) return 'tablet';
  return 'mobile';
};

const useBreakpoint = (): Breakpoint => {
  const [bp, setBp] = useState<Breakpoint>(getBreakpoint);
  useEffect(() => {
    const handler = () => setBp(getBreakpoint());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return bp;
};

// ---------- Helpers ----------

const getDisplayName = (u: Pick<Usuario, 'nome' | 'sobrenome' | 'username' | 'email'>): string => {
  if (u.nome) return `${u.nome} ${u.sobrenome || ''}`.trim();
  return u.username || u.email || 'Sem identificação';
};

const getInitials = (u: Pick<Usuario, 'nome' | 'sobrenome' | 'username' | 'email'>): string => {
  const name = getDisplayName(u);
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatPhone = (raw?: string): string => {
  if (!raw) return '';
  const d = raw.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return raw;
};

const cleanPhone = (raw?: string) => (raw || '').replace(/\D/g, '');

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (d: string) => new Date(d).toLocaleDateString('pt-BR');
const formatDateTime = (d: string) =>
  new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

const ROLE_STYLES: Record<string, { label: string; className: string }> = {
  gestao: { label: 'Gestão', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  colaborador: { label: 'Colaborador', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
};
const getRoleStyle = (role: string) =>
  ROLE_STYLES[role?.toLowerCase()] ?? { label: role || 'Usuário', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };

const STATUS_STYLES: Record<string, string> = {
  entregue: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  concluido: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cancelado: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  pendente: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  preparando: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};
const getStatusStyle = (s: string) =>
  STATUS_STYLES[s?.toLowerCase()] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

// ---------- CopyButton ----------

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}
      className="ml-1 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
      title="Copiar"
    >
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
};

// ---------- DetailContent (conteúdo compartilhado entre modal e drawer) ----------

const DetailContent: React.FC<{ detail: UserDetail; loading: boolean }> = ({ detail, loading }) => {
  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
    </div>
  );

  const u = detail.usuario;
  const phone = cleanPhone(u.telefone);
  const whatsappUrl = phone ? `https://wa.me/55${phone}` : null;
  const roleStyle = getRoleStyle(u.role || u.nivel || '');

  return (
    <>
      {/* Perfil */}
      <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="h-14 w-14 rounded-full bg-blue-500 flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
          {getInitials(u)}
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">{getDisplayName(u)}</h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${roleStyle.className}`}>
              <Shield className="h-3 w-3" />{roleStyle.label}
            </span>
            <span className="text-xs text-gray-400">Desde {formatDate(u.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-700 border-b border-gray-200 dark:border-gray-700">
        {[
          { label: 'Pedidos', value: detail.stats.total_pedidos.toString() },
          { label: 'Total gasto', value: formatCurrency(detail.stats.total_gasto) },
          {
            label: 'Ticket médio',
            value: detail.stats.total_pedidos > 0
              ? formatCurrency(detail.stats.total_gasto / detail.stats.total_pedidos) : '—'
          },
        ].map(({ label, value }) => (
          <div key={label} className="px-3 py-3 text-center">
            <p className="text-base font-bold text-gray-900 dark:text-white leading-tight">{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Contato */}
      <section className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Contato</h3>
        <div className="space-y-2">
          {u.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{u.email}</span>
              <CopyButton text={u.email} />
            </div>
          )}
          {u.username && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">{u.username}</span>
            </div>
          )}
          {u.telefone ? (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">{formatPhone(u.telefone)}</span>
              <CopyButton text={u.telefone} />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-400">
              <Phone className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm italic">Sem telefone</span>
            </div>
          )}
        </div>

        {whatsappUrl ? (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl
              bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-sm font-semibold transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            Abrir no WhatsApp
          </a>
        ) : (
          <div className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl
            bg-gray-100 dark:bg-gray-800 text-gray-400 text-sm cursor-not-allowed select-none">
            <MessageCircle className="h-4 w-4" />
            WhatsApp indisponível
          </div>
        )}
      </section>

      {/* Endereço */}
      {(u.rua || u.bairro || u.cidade) && (
        <section className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Endereço</h3>
          <div className="flex gap-2">
            <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
              {u.rua && <p>{u.rua}{u.numero_casa ? `, ${u.numero_casa}` : ''}</p>}
              {u.bairro && <p>{u.bairro}{u.cidade ? ` — ${u.cidade}` : ''}</p>}
              {u.cep && <p className="text-gray-400 text-xs">CEP: {u.cep}</p>}
            </div>
          </div>
        </section>
      )}

      {/* Pedidos */}
      <section className="px-5 py-4">
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
          <ShoppingBag className="h-3.5 w-3.5" />
          Histórico de Pedidos
          <span className="ml-auto font-bold text-gray-700 dark:text-gray-300 normal-case text-xs">
            {detail.pedidos.length} pedido{detail.pedidos.length !== 1 ? 's' : ''}
          </span>
        </h3>

        {detail.pedidos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <ShoppingBag className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm">Nenhum pedido encontrado</p>
          </div>
        ) : (
          <div className="space-y-3 pb-4">
            {detail.pedidos.map((pedido) => (
              <div key={pedido.id} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/60">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(pedido.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {pedido.status && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${getStatusStyle(pedido.status)}`}>
                        {pedido.status}
                      </span>
                    )}
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(pedido.total)}</span>
                  </div>
                </div>

                <div className="px-3 py-2 space-y-1">
                  {pedido.itens.length > 0 ? pedido.itens.map((item, idx) => (
                    <div key={idx} className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          <span className="font-medium">{item.quantidade}x</span> {item.produto_nome || 'Item'}
                        </span>
                        {item.observacoes && <p className="text-xs text-gray-400 truncate">{item.observacoes}</p>}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {formatCurrency(item.preco_unitario * item.quantidade)}
                      </span>
                    </div>
                  )) : <p className="text-xs text-gray-400 italic">Itens não disponíveis</p>}
                </div>

                {(pedido.metodo_pagamento || pedido.tipo_pedido) && (
                  <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-700">
                    {pedido.tipo_pedido && <span className="text-xs text-gray-400 capitalize">{pedido.tipo_pedido}</span>}
                    {pedido.metodo_pagamento && (
                      <div className="flex items-center gap-1 text-gray-400">
                        <CreditCard className="h-3 w-3" />
                        <span className="text-xs capitalize">{pedido.metodo_pagamento}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

// ---------- UserDetailPanel (responsivo: modal/drawer/bottom-sheet) ----------

interface UserDetailPanelProps {
  open: boolean;
  onClose: () => void;
  userId: string | number | null;
}

const UserDetailPanel: React.FC<UserDetailPanelProps> = ({ open, onClose, userId }) => {
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const bp = useBreakpoint();

  useEffect(() => {
    if (!open || !userId) return;
    setDetail(null);
    setLoading(true);
    api.get(`/api/users/${userId}/detail`)
      .then((r) => setDetail(r.data))
      .catch(() => toast.error('Erro ao carregar detalhes do usuário'))
      .finally(() => setLoading(false));
  }, [open, userId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // ---- Desktop: Modal centralizado ----
  if (bp === 'desktop') {
    return (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center p-6 transition-all duration-300
          ${open ? 'bg-black/50 opacity-100' : 'bg-black/0 opacity-0 pointer-events-none'}`}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className={`bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh]
            flex flex-col overflow-hidden transform transition-all duration-300
            ${open ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'}`}
        >
          {/* Header modal */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Perfil do Usuário
            </span>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Conteúdo com scroll — 2 colunas no desktop */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
              </div>
            ) : detail ? (
              <DetailContent detail={detail} loading={false} />
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  // ---- Tablet: Drawer lateral direito ----
  if (bp === 'tablet') {
    return (
      <>
        <div
          className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={onClose}
        />
        <div
          className={`fixed top-0 right-0 h-full w-[420px] bg-white dark:bg-gray-900 shadow-2xl z-50
            flex flex-col transform transition-transform duration-300 ease-out
            ${open ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Perfil do Usuário</span>
            <button onClick={onClose} className="p-1.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
            ) : detail ? (
              <DetailContent detail={detail} loading={false} />
            ) : null}
          </div>
        </div>
      </>
    );
  }

  // ---- Mobile: Bottom sheet ----
  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 z-50
          rounded-t-3xl shadow-2xl flex flex-col transition-transform duration-300 ease-out
          ${open ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ maxHeight: '90dvh' }}
      >
        {/* Handle indicator */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Perfil do Usuário</span>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
          ) : detail ? (
            <DetailContent detail={detail} loading={false} />
          ) : null}
        </div>
      </div>
    </>
  );
};

// ---------- UsuariosPage ----------

type TabType = 'usuarios' | 'garcons';

const UsuariosPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('usuarios');

  // --- Usuarios state ---
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState<Usuario | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | number | null>(null);
  const [viewingUserId, setViewingUserId] = useState<string | number | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  // --- Garçons state ---
  const [garcons, setGarcons] = useState<Garcom[]>([]);
  const [loadingGarcons, setLoadingGarcons] = useState(true);
  const [showGarcomModal, setShowGarcomModal] = useState(false);
  const [editingGarcom, setEditingGarcom] = useState<Garcom | null>(null);
  const [savingGarcom, setSavingGarcom] = useState(false);
  const [deletingGarcom, setDeletingGarcom] = useState<number | null>(null);
  const [togglingGarcom, setTogglingGarcom] = useState<number | null>(null);
  const [garcomSearch, setGarcomSearch] = useState('');
  const [garcomFilter, setGarcomFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [garcomForm, setGarcomForm] = useState({ nome: '', sobrenome: '', email: '', telefone: '', senha: '', ativo: true });

  useBodyLock(showModal || showGarcomModal);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<UsuarioForm>();

  useEffect(() => { fetchUsuarios(); loadGarcons(); }, []);

  useEffect(() => {
    if (!showModal) return;
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [showModal]);

  // --- Garçons logic ---
  const loadGarcons = async () => {
    try {
      setLoadingGarcons(true);
      const response = await api.get('/api/garcom');
      setGarcons(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.erro || 'Erro ao carregar garçons');
    } finally { setLoadingGarcons(false); }
  };

  const handleGarcomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingGarcom(true);
    try {
      if (editingGarcom) {
        await api.put(`/api/garcom/${editingGarcom.id}`, garcomForm);
        toast.success('Garçom atualizado com sucesso!');
      } else {
        await api.post('/api/garcom', garcomForm);
        toast.success('Garçom criado com sucesso!');
      }
      await loadGarcons();
      resetGarcomForm();
      setShowGarcomModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.erro || error.response?.data?.error || 'Erro ao salvar garçom');
    } finally { setSavingGarcom(false); }
  };

  const handleEditGarcom = (g: Garcom) => {
    setEditingGarcom(g);
    setGarcomForm({ nome: g.nome, sobrenome: g.sobrenome, email: g.email, telefone: g.telefone || '', senha: '', ativo: g.ativo });
    setShowGarcomModal(true);
  };

  const handleDeleteGarcom = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este garçom?')) return;
    setDeletingGarcom(id);
    try {
      await api.delete(`/api/garcom/${id}`);
      toast.success('Garçom excluído com sucesso!');
      await loadGarcons();
    } catch (error: any) {
      toast.error(error.response?.data?.erro || error.response?.data?.error || 'Erro ao excluir garçom');
    } finally { setDeletingGarcom(null); }
  };

  const toggleGarcomStatus = async (id: number, ativo: boolean) => {
    setTogglingGarcom(id);
    try {
      await api.put(`/api/garcom/${id}`, { ativo: !ativo });
      toast.success(ativo ? 'Garçom desativado!' : 'Garçom ativado!');
      await loadGarcons();
    } catch (error: any) {
      toast.error(error.response?.data?.erro || 'Erro ao alterar status');
    } finally { setTogglingGarcom(null); }
  };

  const resetGarcomForm = () => {
    setGarcomForm({ nome: '', sobrenome: '', email: '', telefone: '', senha: '', ativo: true });
    setEditingGarcom(null);
  };

  const filteredGarcons = garcons.filter(g => {
    const fullName = `${g.nome} ${g.sobrenome}`.toLowerCase();
    const matchesSearch = fullName.includes(garcomSearch.toLowerCase()) ||
      g.email.toLowerCase().includes(garcomSearch.toLowerCase()) ||
      (g.telefone && g.telefone.includes(garcomSearch));
    if (garcomFilter === 'active') return matchesSearch && g.ativo;
    if (garcomFilter === 'inactive') return matchesSearch && !g.ativo;
    return matchesSearch;
  });

  const garcomStats = {
    total: garcons.length,
    ativos: garcons.filter(g => g.ativo).length,
    inativos: garcons.filter(g => !g.ativo).length,
  };

  const fetchUsuarios = async () => {
    try {
      const r = await api.get('/api/users');
      setUsuarios(r.data);
    } catch { toast.error('Erro ao carregar usuários'); }
    finally { setLoading(false); }
  };

  const closeModal = useCallback(() => { setShowModal(false); setEditingUsuario(null); reset(); }, [reset]);

  const handleOpenCreate = () => { setEditingUsuario(null); reset(); setShowModal(true); };

  const handleEdit = (u: Usuario) => {
    setEditingUsuario(u);
    reset({ username: u.username || '', email: u.email || '', nome: u.nome || '', sobrenome: u.sobrenome || '', role: u.role || u.nivel || 'usuario', password: '' });
    setShowModal(true);
  };

  const handleViewDetail = (u: Usuario) => { setViewingUserId(u.id); setPanelOpen(true); };
  const handleClosePanel = useCallback(() => { setPanelOpen(false); setTimeout(() => setViewingUserId(null), 350); }, []);

  const onSubmit = async (data: UsuarioForm) => {
    setIsSubmitting(true);
    try {
      if (editingUsuario) {
        await api.put(`/api/users/${editingUsuario.id}`, data);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await api.post('/api/auth/register', data);
        toast.success('Usuário criado com sucesso!');
      }
      closeModal();
      fetchUsuarios();
    } catch (error: any) {
      toast.error(error.response?.data?.error || (editingUsuario ? 'Erro ao atualizar usuário' : 'Erro ao criar usuário'));
    } finally { setIsSubmitting(false); }
  };

  const handleDeleteConfirmed = async (id: string | number) => {
    setDeletingId(id);
    setConfirmDeleteId(null);
    setUsuarios((prev) => prev.filter((u) => u.id !== id));
    try {
      await api.delete(`/api/users/${id}`);
      toast.success('Usuário deletado com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao deletar usuário');
      fetchUsuarios();
    } finally { setDeletingId(null); }
  };

  if (loading && loadingGarcons) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header com Tabs */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {activeTab === 'usuarios' ? 'Usuários' : 'Garçons'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {activeTab === 'usuarios' ? 'Gerencie os usuários do sistema' : 'Cadastre e gerencie os garçons'}
            </p>
          </div>
          {activeTab === 'usuarios' ? (
            <button onClick={handleOpenCreate} className="btn-primary flex items-center gap-2 text-sm whitespace-nowrap">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Novo Usuário</span>
              <span className="sm:hidden">Novo</span>
            </button>
          ) : (
            <button
              onClick={() => { resetGarcomForm(); setShowGarcomModal(true); }}
              className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all text-sm font-medium whitespace-nowrap"
            >
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Novo Garçom</span>
              <span className="sm:hidden">Novo</span>
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('usuarios')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'usuarios'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
          >
            <Shield className="h-4 w-4" />
            Usuários
            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {usuarios.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('garcons')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'garcons'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
          >
            <Users className="h-4 w-4" />
            Garçons
            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {garcons.length}
            </span>
          </button>
        </div>
      </div>

      {/* ===== ABA USUÁRIOS ===== */}
      {activeTab === 'usuarios' && (
        <>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-3 py-4 sm:px-4 sm:py-5 sm:p-6">
              {usuarios.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Users className="h-12 w-12 mb-3 opacity-40" />
                  <p className="text-sm">Nenhum usuário cadastrado ainda.</p>
                  <button onClick={handleOpenCreate} className="mt-4 btn-primary text-sm flex items-center gap-2">
                    <Plus className="h-4 w-4" />Criar primeiro usuário
                  </button>
                </div>
              ) : (
                <ul className="-my-4 divide-y divide-gray-200 dark:divide-gray-700">
                  {usuarios.map((usuario) => {
                    const roleStyle = getRoleStyle(usuario.role || usuario.nivel || '');
                    const isConfirming = confirmDeleteId === usuario.id;
                    const isDeleting = deletingId === usuario.id;
                    return (
                      <li key={usuario.id} className="py-4">
                        <div className="flex items-center gap-3">
                          <button onClick={() => handleViewDetail(usuario)}
                            className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold select-none hover:bg-blue-600 transition-colors flex-shrink-0" title="Ver detalhes">
                            {getInitials(usuario)}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{getDisplayName(usuario)}</p>
                            {(usuario.username || usuario.email) && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate hidden sm:block">
                                {[usuario.username, usuario.email].filter(Boolean).join(' • ')}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${roleStyle.className}`}>
                                <Shield className="h-3 w-3" />{roleStyle.label}
                              </span>
                              <span className="text-xs text-gray-400 hidden sm:inline">Desde {formatDate(usuario.created_at)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <button onClick={() => handleViewDetail(usuario)} className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg" title="Ver detalhes e pedidos">
                              <Eye className="h-4 w-4" />
                            </button>
                            {isConfirming ? (
                              <div className="flex items-center gap-1 ml-1">
                                <span className="text-xs text-gray-500 hidden sm:inline">Confirmar?</span>
                                <button onClick={() => handleDeleteConfirmed(usuario.id)} className="px-2 py-1 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700">Sim</button>
                                <button onClick={() => setConfirmDeleteId(null)} className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg">Não</button>
                              </div>
                            ) : (
                              <>
                                <button onClick={() => handleEdit(usuario)} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg" title="Editar">
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button onClick={() => setConfirmDeleteId(usuario.id)} disabled={isDeleting} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50" title="Excluir">
                                  {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <UserDetailPanel open={panelOpen} onClose={handleClosePanel} userId={viewingUserId} />
        </>
      )}

      {/* ===== ABA GARÇONS ===== */}
      {activeTab === 'garcons' && (
        <>
          {/* Estatísticas */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Users className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Total</p>
                  <p className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{garcomStats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Ativos</p>
                  <p className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{garcomStats.ativos}</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <XCircle className="h-4 w-4 md:h-5 md:w-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Inativos</p>
                  <p className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{garcomStats.inativos}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="text" placeholder="Buscar por nome ou email..." value={garcomSearch}
                  onChange={(e) => setGarcomSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all" />
              </div>
              <select value={garcomFilter} onChange={(e) => setGarcomFilter(e.target.value as any)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all">
                <option value="all">Todos</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </select>
            </div>
          </div>

          {/* Lista de Garçons */}
          {loadingGarcons ? (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : filteredGarcons.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg text-center py-12 px-4">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4 opacity-50" />
              <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-white mb-2">Nenhum garçom encontrado</h3>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-4">
                {garcomSearch || garcomFilter !== 'all' ? 'Tente ajustar os filtros.' : 'Comece cadastrando o primeiro garçom.'}
              </p>
              {!garcomSearch && garcomFilter === 'all' && (
                <button onClick={() => { resetGarcomForm(); setShowGarcomModal(true); }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all text-sm font-medium">
                  <UserPlus className="h-4 w-4" /> Cadastrar Primeiro Garçom
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredGarcons.map((garcom) => (
                  <li key={garcom.id} className="px-3 py-4 sm:px-4 sm:py-5">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 ${garcom.ativo ? 'bg-blue-500' : 'bg-gray-400 dark:bg-gray-600'}`}>
                        {garcom.nome.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{garcom.nome} {garcom.sobrenome}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{garcom.email}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${garcom.ativo
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}>
                            {garcom.ativo ? <><CheckCircle className="h-3 w-3" /> Ativo</> : <><XCircle className="h-3 w-3" /> Inativo</>}
                          </span>
                          {garcom.telefone && <span className="text-xs text-gray-400 hidden sm:inline">{garcom.telefone}</span>}
                          <span className="text-xs text-gray-400 hidden sm:inline">Desde {formatDate(garcom.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button onClick={() => toggleGarcomStatus(garcom.id, garcom.ativo)} disabled={togglingGarcom === garcom.id}
                          className={`p-2 rounded-lg transition-colors ${garcom.ativo ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'} disabled:opacity-50`}
                          title={garcom.ativo ? 'Desativar' : 'Ativar'}>
                          {togglingGarcom === garcom.id ? <Loader2 className="h-4 w-4 animate-spin" /> : garcom.ativo ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        <button onClick={() => handleEditGarcom(garcom)} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg" title="Editar">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDeleteGarcom(garcom.id)} disabled={deletingGarcom === garcom.id}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50" title="Excluir">
                          {deletingGarcom === garcom.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Modal Criar/Editar Usuário */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[95dvh] overflow-y-auto">
            <div className="flex justify-center pt-3 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>
            <div className="p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {editingUsuario ? 'Editar Usuário' : 'Novo Usuário'}
                </h2>
                <button type="button" onClick={closeModal} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome de Usuário</label>
                  <input {...register('username')} className="input-field" placeholder="Nome de usuário" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input {...register('email', { required: 'Email é obrigatório' })} type="email" className="input-field" placeholder="email@exemplo.com" />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
                    <input {...register('nome')} className="input-field" placeholder="Nome" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sobrenome</label>
                    <input {...register('sobrenome')} className="input-field" placeholder="Sobrenome" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Senha {editingUsuario && <span className="text-xs text-gray-500 font-normal">(Deixe em branco para manter)</span>}
                  </label>
                  <input {...register('password', { required: editingUsuario ? false : 'Senha é obrigatória', minLength: { value: 6, message: 'Mínimo 6 caracteres' } })}
                    type="password" className="input-field" placeholder={editingUsuario ? 'Nova senha (opcional)' : 'Digite a senha'} />
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Função</label>
                  <select {...register('role', { required: 'Função é obrigatória' })} className="input-field">
                    <option value="">Selecione uma função</option>
                    <option value="colaborador">Colaborador</option>
                    <option value="gestao">Gestão</option>
                  </select>
                  {errors.role && <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>}
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={isSubmitting} className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-60">
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {editingUsuario ? 'Salvar Alterações' : 'Criar Usuário'}
                  </button>
                  <button type="button" onClick={closeModal} disabled={isSubmitting} className="flex-1 btn-secondary disabled:opacity-60">Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar/Editar Garçom */}
      {showGarcomModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowGarcomModal(false); }}>
          <div className="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[95dvh] overflow-y-auto">
            <div className="flex justify-center pt-3 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>
            <form onSubmit={handleGarcomSubmit}>
              <div className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    {editingGarcom ? 'Editar Garçom' : 'Novo Garçom'}
                  </h2>
                  <button type="button" onClick={() => setShowGarcomModal(false)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome <span className="text-red-500">*</span></label>
                      <input type="text" required value={garcomForm.nome} onChange={(e) => setGarcomForm({ ...garcomForm, nome: e.target.value })}
                        className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all" placeholder="Nome" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sobrenome <span className="text-red-500">*</span></label>
                      <input type="text" required value={garcomForm.sobrenome} onChange={(e) => setGarcomForm({ ...garcomForm, sobrenome: e.target.value })}
                        className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all" placeholder="Sobrenome" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email <span className="text-red-500">*</span></label>
                    <input type="email" required value={garcomForm.email} onChange={(e) => setGarcomForm({ ...garcomForm, email: e.target.value })}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all" placeholder="email@exemplo.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefone <span className="text-gray-500 text-xs">(Opcional)</span></label>
                    <input type="tel" value={garcomForm.telefone} onChange={(e) => setGarcomForm({ ...garcomForm, telefone: e.target.value })}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all" placeholder="Telefone" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Senha {editingGarcom && <span className="text-xs text-gray-500">(deixe em branco para manter)</span>}
                    </label>
                    <input type="password" value={garcomForm.senha} onChange={(e) => setGarcomForm({ ...garcomForm, senha: e.target.value })}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder={editingGarcom ? 'Nova senha (opcional)' : 'Digite a senha'} required={!editingGarcom} />
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" id="garcom-ativo" checked={garcomForm.ativo} onChange={(e) => setGarcomForm({ ...garcomForm, ativo: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                    <label htmlFor="garcom-ativo" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">Garçom ativo</label>
                  </div>
                </div>
                <div className="flex gap-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                  <button type="submit" disabled={savingGarcom}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2.5 rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 text-sm">
                    {savingGarcom ? <><Loader2 className="h-4 w-4 animate-spin" /> Salvando...</> : editingGarcom ? 'Salvar Alterações' : 'Cadastrar Garçom'}
                  </button>
                  <button type="button" onClick={() => setShowGarcomModal(false)}
                    className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 py-2.5 rounded-lg font-medium hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm">
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosPage;
